import { useEffect } from 'react';
import { NEW_FEATURES } from '../../new-features';
import { markSeenById, isSeenById } from '../../hooks/useNewFeatures';

const BADGE_ATTR = 'data-injected-new-badge';

/**
 * Mounts once at the app root.
 *
 * Automatically handles the full lifecycle of "New Feature" badges:
 *
 * 1. **Inject** — whenever an element with `data-feature-id="<id>"` enters the
 *    DOM (or is already present on mount) and the feature is still unseen, a
 *    `position: fixed` NEW badge is appended to `document.body` and positioned
 *    over the top-right corner of the target element via `getBoundingClientRect`.
 *    Because the badge is fixed and lives on body it is never clipped by any
 *    ancestor's `overflow: hidden`.
 *
 * 2. **Track** — an IntersectionObserver shows/hides the badge as the element
 *    scrolls in and out of view.  A scroll + resize listener keeps the fixed
 *    badge anchored to the element at all times.
 *
 * 3. **Dismiss** — once ≥50% of the target is visible for 5 consecutive seconds,
 *    `markSeenById` is called, the badge is removed, and all badge surfaces
 *    (sidebar dot, tab pill, section header) re-render via the shared listener set.
 *
 * Developer workflow:
 *   1. Add an entry to `src/new-features.ts`
 *   2. Add `data-feature-id="<id>"` to the target JSX element
 *      — the badge appears and auto-dismisses with zero additional code.
 */
export function NewFeaturesObserver() {
  useEffect(() => {
    const featureIds = new Set(NEW_FEATURES.map(f => f.id));
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const intersectionObservers = new Map<string, IntersectionObserver>();
    const badgeElements = new Map<string, HTMLSpanElement>();
    const repositioners = new Map<string, () => void>();
    const interactionHandlers = new Map<string, { el: Element; fn: () => void }>();

    // ── Badge factory ─────────────────────────────────────────────────────────

    function createBadgeSpan(id: string): HTMLSpanElement {
      const span = document.createElement('span');
      span.setAttribute(BADGE_ATTR, id);
      span.textContent = 'NEW';
      // position:fixed + viewport-relative coords from getBoundingClientRect —
      // same coordinate space, so the badge tracks the element perfectly and is
      // never clipped by any ancestor overflow:hidden.
      Object.assign(span.style, {
        position: 'fixed',
        display: 'none',          // hidden until IO confirms element is visible
        alignItems: 'center',
        fontSize: '9px',
        fontWeight: '700',
        letterSpacing: '0.05em',
        lineHeight: '1',
        padding: '2px 5px',
        borderRadius: '9999px',
        backgroundColor: '#4f46e5',
        color: '#ffffff',
        zIndex: '9999',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        pointerEvents: 'none',
      } as CSSStyleDeclaration);
      return span;
    }

    // ── Positioning ───────────────────────────────────────────────────────────

    function positionBadge(el: Element, badge: HTMLSpanElement) {
      const rect = el.getBoundingClientRect();
      // Anchor to top-right corner of the target element
      badge.style.top = `${rect.top - 6}px`;
      badge.style.left = `${rect.right - 12}px`;
    }

    // ── Injection ─────────────────────────────────────────────────────────────

    function injectBadge(id: string) {
      if (badgeElements.has(id)) return; // already injected
      const badge = createBadgeSpan(id);
      document.body.appendChild(badge);
      badgeElements.set(id, badge);
    }

    function removeBadge(id: string) {
      const badge = badgeElements.get(id);
      if (badge) {
        badge.remove();
        badgeElements.delete(id);
      }
      const reposition = repositioners.get(id);
      if (reposition) {
        window.removeEventListener('scroll', reposition, { capture: true } as EventListenerOptions);
        window.removeEventListener('resize', reposition);
        repositioners.delete(id);
      }
      const interaction = interactionHandlers.get(id);
      if (interaction) {
        interaction.el.removeEventListener('click', interaction.fn, true);
        interaction.el.removeEventListener('pointerdown', interaction.fn, true);
        interaction.el.removeEventListener('keydown', interaction.fn, true);
        interactionHandlers.delete(id);
      }
    }

    // ── Element setup ─────────────────────────────────────────────────────────

    const setupElement = (el: Element) => {
      const id = el.getAttribute('data-feature-id');
      if (!id || !featureIds.has(id)) return;
      if (isSeenById(id)) return;

      injectBadge(id);

      if (intersectionObservers.has(id)) return; // already watching

      const badge = badgeElements.get(id)!;

      // Reposition listener — keeps the fixed badge tracking the element on scroll/resize
      const reposition = () => {
        if (badge.style.display !== 'none') positionBadge(el, badge);
      };
      window.addEventListener('scroll', reposition, { passive: true, capture: true } as AddEventListenerOptions);
      window.addEventListener('resize', reposition, { passive: true });
      repositioners.set(id, reposition);

      // Interaction listener — any click/key on the element dismisses the badge immediately
      const dismissOnInteraction = () => {
        io.disconnect();
        intersectionObservers.delete(id);
        const t = timers.get(id);
        if (t !== undefined) { clearTimeout(t); timers.delete(id); }
        markSeenById(id);
        removeBadge(id);
      };
      el.addEventListener('click', dismissOnInteraction, true);
      el.addEventListener('pointerdown', dismissOnInteraction, true);
      el.addEventListener('keydown', dismissOnInteraction, true);
      interactionHandlers.set(id, { el, fn: dismissOnInteraction });

      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          if (entry.isIntersecting) {
            positionBadge(el, badge);
            badge.style.display = 'flex';

            // Start 5 s dismiss timer once ≥50% visible (only once per visit)
            if (entry.intersectionRatio >= 0.5 && !timers.has(id)) {
              const timer = setTimeout(() => {
                io.disconnect();
                intersectionObservers.delete(id);
                markSeenById(id);
                removeBadge(id);
                timers.delete(id);
              }, 2000);
              timers.set(id, timer);
            }
          } else {
            // Element scrolled out of view or inside a collapsed accordion
            badge.style.display = 'none';
            const t = timers.get(id);
            if (t !== undefined) {
              clearTimeout(t);
              timers.delete(id);
            }
          }
        },
        { threshold: [0, 0.5] },
      );

      io.observe(el);
      intersectionObservers.set(id, io);
    };

    const cleanupElement = (id: string) => {
      intersectionObservers.get(id)?.disconnect();
      intersectionObservers.delete(id);
      const t = timers.get(id);
      if (t !== undefined) { clearTimeout(t); timers.delete(id); }
      removeBadge(id);
    };

    // ── Initial scan ──────────────────────────────────────────────────────────

    document.querySelectorAll('[data-feature-id]').forEach(setupElement);

    // ── MutationObserver — watch for future DOM changes ───────────────────────

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          const el = node as Element;
          if (el.hasAttribute(BADGE_ATTR)) continue; // skip our own badge elements
          if (el.hasAttribute('data-feature-id')) setupElement(el);
          el.querySelectorAll('[data-feature-id]').forEach(setupElement);
        }
        for (const node of m.removedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          const el = node as Element;
          if (el.hasAttribute(BADGE_ATTR)) continue;
          const id = el.getAttribute('data-feature-id');
          if (id) cleanupElement(id);
          el.querySelectorAll('[data-feature-id]').forEach((child) => {
            const cid = child.getAttribute('data-feature-id');
            if (cid) cleanupElement(cid);
          });
        }
      }
    });

    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      intersectionObservers.forEach((io) => io.disconnect());
      timers.forEach((t) => clearTimeout(t));
      repositioners.forEach((fn) => {
        window.removeEventListener('scroll', fn, { capture: true } as EventListenerOptions);
        window.removeEventListener('resize', fn);
      });
      featureIds.forEach(id => removeBadge(id));
    };
  }, []);

  return null;
}
